import { useState, useContext, useEffect, useMemo } from "react";
import { DateTime } from "luxon";
import { AuthContext, YearContext } from "../../context/context";
import { fetchFinanceData } from "../../api";
import { useLanguage } from "../../context/LanguageContext";
import { useToast } from "../../context/ToastContext";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Card from "./Card";
import { TbEye, TbPlus, TbChevronDown, TbCalendarMonth } from "react-icons/tb";
import BudgetDonutChart from "../Tables/BudgetDonutChart";
import Dropdown from "../Elements/Dropdown";

const CardFinance = ({ className, isInTab = false, customTitle, customDescription }) => {
  const { userData } = useContext(AuthContext);
  const { selectedYear } = useContext(YearContext);
  const { copy } = useLanguage();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [financeData, setFinanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);

  // Add state for selected month
  const currentMonth = DateTime.local().month;
  const currentYear = DateTime.local().year;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  // Reset selected month when year changes
  useEffect(() => {
    // If viewing current year, select current month
    // Otherwise, select January
    if (selectedYear === currentYear) {
      setSelectedMonth(currentMonth);
    } else {
      setSelectedMonth(1); // January
    }
  }, [selectedYear, currentYear, currentMonth]);

  // Fetch finance data for the selected year
  useEffect(() => {
    const getFinanceCacheKey = (year) => {
      return userData?.id ? `finance_cache_${userData.id}_${year}` : null;
    };

    const getCachedFinanceData = (year) => {
      try {
        const cacheKey = getFinanceCacheKey(year);
        if (!cacheKey) return null;

        const cachedData = localStorage.getItem(cacheKey);
        if (!cachedData) return null;

        const parsedData = JSON.parse(cachedData);
        const now = new Date().getTime();
        const cacheExpiry = 60 * 60 * 1000; // 1 hour in milliseconds

        // Check if cache is still valid (less than 1 hour old)
        if (now - parsedData.timestamp < cacheExpiry) {
          return parsedData.data;
        } else {
          // Cache expired, remove it
          localStorage.removeItem(cacheKey);
          return null;
        }
      } catch (error) {
        console.error('Error reading cached finance data:', error);
        return null;
      }
    };

    const fetchData = async () => {
      // Wait for selectedYear to be set
      if (!selectedYear) return;

      // If userData.id is not available yet, show loading state
      if (!userData?.id) {
        setLoading(true);
        return;
      }

      // First, try to get data from cache
      const cachedData = getCachedFinanceData(selectedYear);
      if (cachedData) {
        setFinanceData(cachedData);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetchFinanceData(selectedYear);

        if (response && response.data) {
          setFinanceData(response.data);
        } else {
          setFinanceData([]);
        }
      } catch (error) {
        console.error('Error fetching finance data:', error);
        if (!error.message?.includes('not found')) {
          showToast(`Error loading finance data: ${error.message}`, "error");
        }
        setFinanceData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, userData?.id, showToast]);

  // All months
  const months = [
    { number: 1, name: "Jan", fullName: "January" },
    { number: 2, name: "Feb", fullName: "February" },
    { number: 3, name: "Mar", fullName: "March" },
    { number: 4, name: "Apr", fullName: "April" },
    { number: 5, name: "May", fullName: "May" },
    { number: 6, name: "Jun", fullName: "June" },
    { number: 7, name: "Jul", fullName: "July" },
    { number: 8, name: "Aug", fullName: "August" },
    { number: 9, name: "Sep", fullName: "September" },
    { number: 10, name: "Oct", fullName: "October" },
    { number: 11, name: "Nov", fullName: "November" },
    { number: 12, name: "Dec", fullName: "December" }
  ];

  // Get data for selected month
  const selectedMonthData = useMemo(() => {
    if (!financeData || financeData.length === 0) return null;

    const monthData = financeData.find(
      f => f.year === selectedYear && f.month === selectedMonth
    );

    return monthData || null;
  }, [financeData, selectedYear, selectedMonth]);
  

  const cardTitle = customTitle || (isInTab
    ? "Finances"
    : "Finances");

  const cardDescription = customDescription || (isInTab
    ? "Monthly financial overview and budget tracking"
    : "Monthly financial overview and budget tracking");

  const financeDropdown = [
    {
      text: copy?.dropdown?.view_all || "View all",
      onClick: () => {
        // Set the selected month in localStorage before navigating
        const selectedMonthName = months.find(m => m.number === selectedMonth)?.fullName || months[currentMonth - 1].fullName;
        const storageKey = userData?.id ? `selectedMonth_user_${userData.id}` : 'selectedMonth';
        localStorage.setItem(storageKey, selectedMonthName);
        navigate('/finance');
      },
      icon: <TbEye />,
    },
  ];


  if (loading) {
    return (
      <Card
        title={cardTitle}
        description={cardDescription}
        className={`${className || ""}`}
      >
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Card>
    );
  }

  // Check if there's any finance data at all
  const hasAnyFinanceData = financeData && financeData.length > 0;

  const selectedMonthName = months.find(m => m.number === selectedMonth)?.fullName || months[currentMonth - 1].fullName;

  // Helper function to navigate to finance page with specific month
  const navigateToFinanceWithMonth = () => {
    // Set the selected month in localStorage before navigating
    const storageKey = userData?.id ? `selectedMonth_user_${userData.id}` : 'selectedMonth';
    localStorage.setItem(storageKey, selectedMonthName);
    navigate('/finance');
  };

  // If no data exists at all, show empty state
  if (!loading && !hasAnyFinanceData) {
    return (
      <Card
        title={cardTitle}
        description={cardDescription}
        options={financeDropdown}
        className={`${className || ""}`}
      >
        <div className="bg-blue-100 rounded-lg flex items-center justify-center py-8">
          <button
            className="w-10 h-10 rounded-md flex items-center justify-center shadow-sm bg-white card-base-color transition-colors duration-200"
            onClick={navigateToFinanceWithMonth}
            aria-label="Add finance data"
          >
            <TbPlus className="text-2xl" />
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={cardTitle}
      description={cardDescription}
      options={financeDropdown}
      className={`${className || ""}`}
    >
      {/* Month Dropdown Filter */}
      <div className="flex items-center justify-start pb-3">
        <div className="flex items-center justify-center gap-1 bg-white pl-3 pr-1 py-1 rounded-full card-base-border">
          <TbCalendarMonth size={20} />
          <div className="flex gap-1 items-center">
            <span className="text-sm">{selectedMonthName}</span>
            <Dropdown
              icon={TbChevronDown}
              rotateOnOpen={true}
              isOpen={isMonthDropdownOpen}
              onOpenChange={setIsMonthDropdownOpen}
              options={months.map((month) => ({
                text: month.fullName,
                onClick: () => {
                  setSelectedMonth(month.number);
                  setIsMonthDropdownOpen(false);
                },
              }))}
            />
          </div>
        </div>
      </div>

      {/* Budget Visualization */}
      <div className="mt-2">
        {selectedMonthData ? (
          <BudgetDonutChart
            monthData={selectedMonthData}
            currency={selectedMonthData?.currency || 'EUR'}
            showHeader={false}
          />
        ) : (
          <div className="bg-blue-100 rounded-lg flex items-center justify-center py-8">
            <button
              className="w-10 h-10 rounded-md flex items-center justify-center shadow-sm bg-white card-base-color transition-colors duration-200"
              onClick={navigateToFinanceWithMonth}
              aria-label="Add finance data"
            >
              <TbPlus className="text-2xl" />
            </button>
          </div>
        )}
      </div>
    </Card>
  );
};

CardFinance.propTypes = {
  className: PropTypes.string,
  isInTab: PropTypes.bool,
  customTitle: PropTypes.string,
  customDescription: PropTypes.string,
};

export default CardFinance;
